import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  ArrowDownTrayIcon, 
  DocumentTextIcon, 
  PhotoIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';

const ExportPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('export');
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState('xls');
  const [error, setError] = useState(null);
  
  // Form state for export options
  const [exportOptions, setExportOptions] = useState({
    listId: '',
    status: '',
    category: '',
    includeImages: true,
    includeItemInfo: true,
    format: 'auctioneer' // Default to Auctioneer Software format
  });
  
  // Pagination state for history tab
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });
  
  // Fetch active lists for selection
  const { data: lists } = useQuery('activeLists', async () => {
    try {
      const response = await api.get('/api/lists/active');
      return response.data;
    } catch (err) {
      console.error('Error fetching lists:', err);
      return [];
    }
  });
  
  // Fetch categories for filter
  const { data: categories } = useQuery('categories', async () => {
    try {
      const response = await api.get('/api/inventory/categories');
      return response.data;
    } catch (err) {
      console.error('Error fetching categories:', err);
      return [];
    }
  });
  
  // Fetch export history
  const {
    data: exportHistory,
    isLoading: isLoadingHistory,
    refetch: refetchHistory
  } = useQuery(
    ['exportHistory', pagination],
    async () => {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      
      const response = await api.get(`/api/exports/history?${params}`);
      return response.data;
    },
    {
      enabled: activeTab === 'history'
    }
  );
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExportOptions(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle export action
  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);
      
      // Different endpoints and parameters based on export type
      const endpoint = exportType === 'xls' ? '/api/exports/xls' : '/api/exports/images';
      const data = exportType === 'xls' ? {
        listId: exportOptions.listId || null,
        status: exportOptions.status || null,
        category: exportOptions.category || null,
        includeImages: exportOptions.includeImages,
        format: exportOptions.format
      } : {
        listId: exportOptions.listId || null,
        includeItemInfo: exportOptions.includeItemInfo
      };
      
      // Make the export request with responseType blob to handle file download
      const response = await api.post(endpoint, data, { responseType: 'blob' });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/["']/g, '')
        : exportType === 'xls' ? 'inventory-export.xlsx' : 'inventory-images.zip';
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Refresh history after successful export
      if (activeTab === 'history') {
        refetchHistory();
      }
      
      // Show success message
      alert('Export completed successfully!');
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to generate export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Handle downloading an exported file from history
  const handleDownloadExport = async (exportId) => {
    try {
      const response = await api.get(`/api/exports/download/${exportId}`, { responseType: 'blob' });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/["']/g, '')
        : 'export-file';
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file. Please try again.');
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Export Inventory</h1>
          <p className="mt-1 text-sm text-gray-500">
            Export your inventory data to Excel or download images as a ZIP archive.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('export')}
            className={`${activeTab === 'export' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Create Export
          </button>

          <button
            onClick={() => setActiveTab('history')}
            className={`${activeTab === 'history' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Export History
          </button>
        </nav>
      </div>

      {/* Export tab content */}
      {activeTab === 'export' && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-6">
              {/* Export Type Selection */}
              <div>
                <label className="text-base font-medium text-gray-900">Export Type</label>
                <p className="text-sm text-gray-500">Select the type of export you want to create.</p>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center">
                    <input
                      id="export-xls"
                      name="export-type"
                      type="radio"
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={exportType === 'xls'}
                      onChange={() => setExportType('xls')}
                    />
                    <label htmlFor="export-xls" className="ml-3 block text-sm font-medium text-gray-700">
                      <span className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-400" />
                        Excel Spreadsheet (XLS)
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">
                        Export inventory data to a spreadsheet for analysis or integration with other systems.
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="export-images"
                      name="export-type"
                      type="radio"
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={exportType === 'images'}
                      onChange={() => setExportType('images')}
                    />
                    <label htmlFor="export-images" className="ml-3 block text-sm font-medium text-gray-700">
                      <span className="flex items-center">
                        <PhotoIcon className="h-5 w-5 mr-2 text-gray-400" />
                        Images ZIP Archive
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">
                        Download all images for your inventory items in a ZIP file.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  {/* Export Format Selection (only for XLS exports) */}
                  {exportType === 'xls' && (
                    <div className="sm:col-span-3">
                      <label htmlFor="format" className="block text-sm font-medium text-gray-700">
                        Export Format
                      </label>
                      <div className="mt-1">
                        <select
                          id="format"
                          name="format"
                          className="form-input"
                          value={exportOptions.format}
                          onChange={handleInputChange}
                        >
                          <option value="auctioneer">Auctioneer Software</option>
                          <option value="hibid">AuctionFlex/HiBid</option>
                          <option value="standard">Standard Format</option>
                        </select>
                      </div>
                    </div>
                  )}
                  
                  {/* List Selection */}
                  <div className="sm:col-span-3">
                    <label htmlFor="listId" className="block text-sm font-medium text-gray-700">
                      List
                    </label>
                    <div className="mt-1">
                      <select
                        id="listId"
                        name="listId"
                        className="form-input"
                        value={exportOptions.listId}
                        onChange={handleInputChange}
                      >
                        <option value="">All Lists</option>
                        {lists?.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.name} {list.defaultList && '(Default)'}
                            {user.role !== 'USER' && list.owner && ` - ${list.owner}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {exportType === 'xls' && (
                    <>
                      {/* Status Filter */}
                      <div className="sm:col-span-3">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          Status Filter
                        </label>
                        <div className="mt-1">
                          <select
                            id="status"
                            name="status"
                            className="form-input"
                            value={exportOptions.status}
                            onChange={handleInputChange}
                          >
                            <option value="">All Statuses</option>
                            <option value="AVAILABLE">Available</option>
                            <option value="RESERVED">Reserved</option>
                            <option value="SOLD">Sold</option>
                            <option value="ARCHIVED">Archived</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Category Filter */}
                      <div className="sm:col-span-3">
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category Filter
                        </label>
                        <div className="mt-1">
                          <select
                            id="category"
                            name="category"
                            className="form-input"
                            value={exportOptions.category}
                            onChange={handleInputChange}
                          >
                            <option value="">All Categories</option>
                            {categories?.map(category => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Include Images Option */}
                      <div className="sm:col-span-6">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="includeImages"
                              name="includeImages"
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              checked={exportOptions.includeImages}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="includeImages" className="font-medium text-gray-700">
                              Include Image URLs
                            </label>
                            <p className="text-gray-500">
                              Add image URLs to the exported spreadsheet for reference.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {exportType === 'images' && (
                    <div className="sm:col-span-6">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="includeItemInfo"
                            name="includeItemInfo"
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={exportOptions.includeItemInfo}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="includeItemInfo" className="font-medium text-gray-700">
                            Include Item Information
                          </label>
                          <p className="text-gray-500">
                            Add a text file with item details alongside each image in the ZIP file.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="btn-primary"
              >
                {isExporting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Create Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* History tab content */}
      {activeTab === 'history' && (
        <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
          {isLoadingHistory ? (
            <div className="py-10 text-center text-gray-500">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
              </div>
              <p className="mt-2">Loading export history...</p>
            </div>
          ) : exportHistory?.exports?.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No export history</h3>
              <p className="mt-1 text-sm text-gray-500">
                You haven't created any exports yet.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('export')}
                  className="btn-primary"
                >
                  <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                  Create Your First Export
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Export File</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">List</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Items</th>
                    {(user.role === 'COMPANY_ADMIN' || user.role === 'SUPER_ADMIN') && (
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created By</th>
                    )}
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Download</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {exportHistory.exports.map((exportItem) => (
                    <tr key={exportItem.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {exportItem.fileName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {exportItem.type === 'XLS' ? (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                            <DocumentTextIcon className="-ml-0.5 mr-1 h-4 w-4" />
                            Excel
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                            <PhotoIcon className="-ml-0.5 mr-1 h-4 w-4" />
                            Images
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {exportItem.listName || 'All Lists'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {exportItem.itemCount}
                      </td>
                      {(user.role === 'COMPANY_ADMIN' || user.role === 'SUPER_ADMIN') && (
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {exportItem.exporterName}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {formatDate(exportItem.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {exportItem.status === 'COMPLETED' ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            <CheckCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                            Complete
                          </span>
                        ) : exportItem.status === 'FAILED' ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                            <ExclamationCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                            Failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                            <ClockIcon className="-ml-0.5 mr-1 h-4 w-4" />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        {exportItem.status === 'COMPLETED' && (
                          <button
                            onClick={() => handleDownloadExport(exportItem.id)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
                            <span className="sr-only">Download {exportItem.fileName}</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {exportHistory && exportHistory.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, exportHistory.totalPages) }))}
                  disabled={pagination.page === exportHistory.totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{exportHistory.exports.length > 0 ? ((pagination.page - 1) * pagination.limit) + 1 : 0}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, exportHistory.total)}
                    </span>{' '}
                    of <span className="font-medium">{exportHistory.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {/* Page numbers */}
                    {Array.from({ length: exportHistory.totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setPagination(prev => ({ ...prev, page }))}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${page === pagination.page ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600' : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'}`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, exportHistory.totalPages) }))}
                      disabled={pagination.page === exportHistory.totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportPage;
